import { Box, ClickAwayListener, Typography } from '@mui/material'
import TagsApi, { tagProps } from 'api/setting/personnel-managements/tags.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import HelperText from 'component/Input/HelperText'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import { colors } from 'constants/theme'
import SwitchButton from 'new-components/buttons/switch-button'
import TextInput from 'new-components/inputs/text-input'
import PageModal from 'new-components/modal/page-modal'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormManageColor from './form-manage-color'

const initStateErrorMessage = {
    TAG_NAME: ``,
    IS_DUPLICATE_TAG_NAME: ``,
    TAG_COLOR: ``
}

type FormManageTagProps = {
    isOpen: boolean
    onClosePopTag: () => void
    isEdit: boolean
    data: tagProps
    reLoadData: () => void
}

const FormManageTag = (props: FormManageTagProps) => {
    const { isOpen, onClosePopTag, isEdit, data, reLoadData } = props
    const { t } = useTranslation()

    const [tagId, setTagId] = useState<number>(0)
    const [tagName, setTagName] = useState<string>('')
    const [tagColor, setTagColor] = useState<string>('')
    const [status, setStaus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE')
    const [popSelectColor, setPopSelectColor] = useState<boolean>(false)
    const [disabledSubmit, setDisabledSubmit] = useState(false)
    const [operativeTypes, setOperativeTypes] = useState<any[]>([])
    const [operativeType, setOperativeType] = useState<any>({})
    const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

    const onLoadOperativeType = async (search = '') => {
        const resp = await OperativeTypeApi.findAll({ pageLimit: 100, search, status: 'ACTIVE' })
        if (resp.status === 200) setOperativeTypes(resp.data || [])
    }

    useEffect(() => {
        onLoadOperativeType()
    }, [])

    useEffect(() => {
        if (isEdit && data) {
            setTagId(data.tagId || 0)
            setTagName(data.tagName)
            setTagColor(data.color)
            setStaus(data.status || 'ACTIVE')
            if (data.operativeTypeId) {
                setOperativeType({
                    operativeTypeId: data.operativeTypeId,
                    operativeTypeName: data.operativeTypeName,
                    operativeTypeNameEn: data.operativeTypeNameEn
                })
            }
            setErrorMessage(initStateErrorMessage)
        }
    }, [data, isEdit])

    // ฟังก์ชันเคลียร์ state ทั้งหมด แล้วปิด popup
    const resetForm = () => {
        setTagId(0)
        setTagName('')
        setTagColor('')
        setStaus('ACTIVE')
        setOperativeType({})
        setErrorMessage(initStateErrorMessage)
        onClosePopTag()
    }

    const onSubmit = async () => {
        // ตรวจสอบความถูกต้อง
        if (!tagName || !tagColor || tagColor === '#') {
            return setErrorMessage({
                ...errorMessage,
                TAG_NAME: !tagName ? t('TAGS.FORM.TAGNAME_PLEASE') : '',
                TAG_COLOR: (!tagColor || tagColor === '#') ? t('TAGS.FORM.TAGCOLOR_PLEASE') : ''
            })
        }

        const payload: tagProps = {
            tagName,
            tagNameEn: tagName,
            color: tagColor,
            operativeTypeId: operativeType?.operativeTypeId || null
        }

        setDisabledSubmit(true)
        if (!isEdit) {
            try {
                await TagsApi.createTag(payload)
                notiSuccess(t('TAGS.MESSAGE.ADD_SUCCESSFUL'))
                resetForm()
                reLoadData()
            } catch (err: any) {
                const errMsg = err?.response?.data
                if (errMsg?.message === 'IS_DUPLICATE_TAG_NAME') {
                    setErrorMessage({
                        ...errorMessage,
                        IS_DUPLICATE_TAG_NAME: t(`TAGS.MESSAGE.${errMsg.message}`)
                    })
                } else {
                    notiError(t('TAGS.MESSAGE.ERROR'))
                }
            } finally {
                setDisabledSubmit(false)
            }
        } else {
            try {
                await TagsApi.updateTag(tagId, { ...payload, status })
                notiSuccess(t('TAGS.MESSAGE.UPDATE_SUCCESSFUL'))
                resetForm()
                reLoadData()
            } catch (err: any) {
                const errMsg = err?.response?.data
                if (errMsg?.message === 'IS_DUPLICATE_TAG_NAME') {
                    setErrorMessage({
                        ...errorMessage,
                        IS_DUPLICATE_TAG_NAME: t(`TAGS.MESSAGE.${errMsg.message}`)
                    })
                } else {
                    notiError(t('TAGS.MESSAGE.ERROR'))
                }
            } finally {
                setDisabledSubmit(false)
            }
        }
    }

    return (
        <Box>
            <PageModal
                title={t(`TAGS.TITLE_${isEdit ? 'UPDATE' : 'CREATE'}`)}
                isOpen={isOpen}
                onCancel={{ onClick: resetForm }}
                onSubmit={{ onClick: onSubmit, disabled: disabledSubmit }}
                handleClose={resetForm}
                size='XS'
            >
                <Box display='flex' flexDirection='column' gap='8px'>
                    {isEdit && (
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                            <SwitchButton
                                checked={status === 'ACTIVE'}
                                onChange={() => setStaus(status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                            />
                        </Box>
                    )}

                    <Box>
                        <Box sx={{ textAlign: 'right', fontSize: '10px' }}>{tagName.length}/20</Box>
                        <TextInput
                            fullWidth
                            required
                            label={t('TAGS.FORM.TAGNAME')}
                            value={tagName}
                            onChange={e => {
                                const v = e.target.value
                                if (v.length <= 20) {
                                    setTagName(v)
                                    setErrorMessage(prev => ({ ...prev, TAG_NAME: '', IS_DUPLICATE_TAG_NAME: '' }))
                                }
                            }}
                            helperText={errorMessage.IS_DUPLICATE_TAG_NAME || errorMessage.TAG_NAME}
                        />
                    </Box>

                    <Box pt='8px'>
                        <Box>
                            {t('TAGS.FORM.TAGCOLOR')}
                            <span style={{ color: colors.statusInactive, marginLeft: 3 }}>*</span>
                        </Box>
                        <ClickAwayListener onClickAway={() => setPopSelectColor(false)}>
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    onClick={() => setPopSelectColor(prev => !prev)}
                                    sx={{
                                        height: 43,
                                        borderRadius: 1,
                                        border: errorMessage.TAG_COLOR && (!tagColor || tagColor === '#')
                                            ? `1px solid ${colors.statusInactive}`
                                            : '1px solid #0000001f',
                                        backgroundColor: tagColor || 'transparent',
                                        cursor: 'pointer'
                                    }}
                                />
                                {errorMessage.TAG_COLOR && <HelperText label={errorMessage.TAG_COLOR} />}
                                {popSelectColor && (
                                    <FormManageColor
                                        color={tagColor}
                                        setColor={setTagColor}
                                        handleChangeColor={({ hex }) => {
                                            setErrorMessage(prev => ({ ...prev, TAG_COLOR: '' }))
                                            setTagColor(hex)
                                            setOperativeType({})
                                        }}
                                        onLoadOperativeType={onLoadOperativeType}
                                        operativeTypes={operativeTypes}
                                        operativeType={operativeType}
                                        handleChangeOperative={val => setOperativeType(val)}
                                    />
                                )}
                            </Box>
                        </ClickAwayListener>
                    </Box>
                </Box>
            </PageModal>
        </Box>
    )
}

export default FormManageTag
